import { useState } from "react";

/**
 * The two marks, and the rule that keeps them apart.
 *
 * Plan §0 is unusually firm about this and for a good reason: ASETQUHub is the
 * application and ASETQU/ASQ is the asset, and a user who cannot tell which
 * mark is which cannot tell what they are being asked to spend. So there are
 * two components, they take no `variant` prop, and neither can be talked into
 * rendering the other.
 *
 * # Where the files come from
 *
 * The source art lives in `apps/hub/assets/` and is never read directly. What
 * ships is `public/brand/`, built by `scripts/build-brand-assets.py` — which
 * exists because the coin master is 2048x2048 and 1.4 MB, and the header draws
 * it at 13px. See that script for the size ladder and why the derivatives are
 * WebP.
 *
 * Both components keep an `onError` fallback. Not defensive clutter: the
 * derivatives are generated, and a checkout where somebody has not run the
 * script should render a legible placeholder rather than a broken-image glyph
 * in the middle of the navbar.
 */

const HUB_LOGO = "/brand/asetquhub-logo.webp";

/**
 * The coin, at every size the app draws it.
 *
 * Each step is chosen so the smallest one at least twice the requested size is
 * used — enough for a retina display and no more. A 1024px asset behind a 20px
 * icon is 300 KB of detail nobody can see.
 */
const TOKEN_STEPS = [64, 256, 1024] as const;

function tokenSource(size: number): string {
  const wanted = size * 2;
  const step = TOKEN_STEPS.find((candidate) => candidate >= wanted) ?? TOKEN_STEPS.at(-1)!;
  return `/brand/asetqu-token-${step}.webp`;
}

/**
 * The platform mark. Header, landing, footer, favicon, loading — anywhere the
 * application identifies itself.
 *
 * Never the token coin. That substitution is the specific mistake §0 names.
 */
export function HubMark({ size = 32 }: { size?: number }) {
  const [missing, setMissing] = useState(false);

  if (missing) {
    return (
      <span
        className="flex items-center justify-center border border-hub-500/60 font-display text-hub-500"
        style={{ width: size, height: size, fontSize: size * 0.5 }}
        aria-hidden="true"
      >
        A
      </span>
    );
  }

  return (
    <img
      src={HUB_LOGO}
      alt=""
      width={size}
      height={size}
      className="block object-contain"
      style={{ width: size, height: size }}
      onError={() => setMissing(true)}
    />
  );
}

/** The full lockup: mark plus wordmark. */
export function HubLockup({ size = 30 }: { size?: number }) {
  return (
    <span className="flex items-center gap-2.5">
      <HubMark size={size} />
      <span
        className="font-display uppercase tracking-tight text-paper-50"
        style={{ fontSize: size * 0.62, fontVariationSettings: '"wght" 700, "wdth" 92' }}
      >
        ASETQU<span className="text-hub-500">Hub</span>
      </span>
      <span className="sr-only">ASETQUHub</span>
    </span>
  );
}

/**
 * The token mark. Balances, prices, vault figures, payment summaries.
 *
 * Never in the navbar brand slot. That is the other half of §0's rule.
 *
 * `decorative` drops it from the accessibility tree, for the places where the
 * coin is a visual object rather than a label — the hero, where it sits at 7%
 * opacity behind the headline. Everywhere else it carries its alt text, because
 * next to a bare number the mark is what says which token.
 */
export function TokenMark({
  size = 20,
  decorative = false,
}: {
  size?: number;
  decorative?: boolean;
}) {
  const [missing, setMissing] = useState(false);

  if (missing) {
    return (
      <span
        className="inline-block shrink-0 rounded-full border border-asq-gold/70 bg-asq-gold/15"
        style={{ width: size, height: size }}
        {...(decorative
          ? { "aria-hidden": true as const }
          : { role: "img", "aria-label": "ASETQU token" })}
      />
    );
  }

  return (
    <img
      src={tokenSource(size)}
      alt={decorative ? "" : "ASETQU token"}
      width={size}
      height={size}
      // Lazy everywhere but the header, where it is above the fold and a
      // deferred 3 KB request buys nothing but a flash of missing icon.
      loading={size > 64 ? "lazy" : "eager"}
      decoding="async"
      className="block shrink-0 object-contain"
      style={{ width: size, height: size }}
      onError={() => setMissing(true)}
    />
  );
}
